import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../ui/dialog';
import { CalendarClock, Plus, Trash2, Search, Send, XCircle, AlertCircle, Loader2, Zap, Layers, ListPlus, Upload } from 'lucide-react';
import api from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { DynamicTable } from '../common/DynamicTable';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import socketService from '../../services/socketService';

import { SelectWithSearch, SelectOption } from '../ui/select-with-search';

interface MensagemAgendada {
  id: string;
  contato: { id: string, nome: string, telefone: string };
  usuario: { id: string, nome: string };
  tipo: string;
  conteudo: string;
  fileName?: string;
  dataAgendamento: string;
  recorrencia?: {
    tipo?: 'diasSemana' | 'intervaloDias';
    diasSemana?: number[];
    intervaloDias?: number;
    horarios: string[];
    dataFim?: string;
  } | null;
  status: 'PENDENTE' | 'PROCESSANDO' | 'ENVIADA' | 'ERRO' | 'CANCELADA';
  motivoErro?: string;
  dataCriacao: string;
  instancia?: { id: string, nome: string };
  instanciaUazapi?: { id: string, nome: string };
}

const DIAS_SEMANA_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

interface AnaliseLista {
  total: number;
  validos: number;
  invalidos: number;
  duplicados: number;
}

/**
 * Espelha a validação do backend (helpers/phoneHelper.js): só dígitos,
 * 8 a 15. Serve para dar feedback antes de enviar, não substitui o servidor.
 */
const analisarListaTelefones = (texto: string): AnaliseLista => {
  const linhas = (texto || '')
    .split(/\r?\n/)
    .flatMap(l => l.split(/[,;\t]+/).map(v => v.trim()).filter(Boolean));

  const vistos = new Set<string>();
  let validos = 0;
  let duplicados = 0;
  let invalidos = 0;

  for (const linha of linhas) {
    const digitos = linha.split('@')[0].replace(/\D/g, '');
    if (digitos.length < 8 || digitos.length > 15) {
      invalidos++;
      continue;
    }
    if (vistos.has(digitos)) {
      duplicados++;
      continue;
    }
    vistos.add(digitos);
    validos++;
  }

  return { total: linhas.length, validos, invalidos, duplicados };
};

const formatRecorrencia = (r: MensagemAgendada['recorrencia']): string => {
  if (!r || !r.horarios?.length) return '';
  const horas = r.horarios.join(', ');
  if (r.tipo === 'intervaloDias' && r.intervaloDias) {
    let texto = `A cada ${r.intervaloDias} dia(s) às ${horas}`;
    if (r.dataFim) {
      texto += ` até ${new Date(r.dataFim).toLocaleDateString('pt-BR')}`;
    }
    return texto;
  }
  if (!r.diasSemana?.length) return '';
  const dias = r.diasSemana.map(d => DIAS_SEMANA_LABELS[d]).join(', ');
  let texto = `${dias} às ${horas}`;
  if (r.dataFim) {
    texto += ` até ${new Date(r.dataFim).toLocaleDateString('pt-BR')}`;
  }
  return texto;
};

export const MensagensAgendadasTable = () => {
  const [mensagens, setMensagens] = useState<MensagemAgendada[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  const [showModal, setShowModal] = useState(false);
  const [loadingForm, setLoadingForm] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [itemToCancel, setItemToCancel] = useState<string | null>(null);

  // Form states
  const [contatosOptions, setContatosOptions] = useState<SelectOption[]>([]);
  const [selectedContacts, setSelectedContacts] = useState<SelectOption[]>([]);
  const [conteudo, setConteudo] = useState('');
  const [dataAgendamento, setDataAgendamento] = useState('');
  const [instancias, setInstancias] = useState<any[]>([]);
  const [instanciaSelecionada, setInstanciaSelecionada] = useState('AUTO');

  // Contact pagination and search states
  const [contatosPage, setContatosPage] = useState(1);
  const [contatosSearch, setContatosSearch] = useState('');
  const [hasMoreContatos, setHasMoreContatos] = useState(true);
  const [loadingContatos, setLoadingContatos] = useState(false);

  // Quick messages & block scheduling
  const [modoAgendamento, setModoAgendamento] = useState<'personalizada' | 'bloco' | 'lista'>('personalizada');
  const [showQuickPanel, setShowQuickPanel] = useState(false);
  const [quickMessages, setQuickMessages] = useState<any[]>([]);
  const [quickMsgsFilter, setQuickMsgsFilter] = useState('');
  const [loadingQuickMsgs, setLoadingQuickMsgs] = useState(false);
  const [blocosDisponiveis, setBlocosDisponiveis] = useState<any[]>([]);
  const [blocoAgendamentoId, setBlocoAgendamentoId] = useState('');
  const [intervaloBloco, setIntervaloBloco] = useState(30);

  // Importação de lista de telefones
  const [listaTelefones, setListaTelefones] = useState('');
  const [arquivoLista, setArquivoLista] = useState<File | null>(null);
  const [intervaloLista, setIntervaloLista] = useState(30);
  const listaFileInputRef = useRef<HTMLInputElement | null>(null);

  // Recorrência
  const [isRecorrente, setIsRecorrente] = useState(false);
  const [tipoRecorrencia, setTipoRecorrencia] = useState<'diasSemana' | 'intervaloDias'>('diasSemana');
  const [diasSemana, setDiasSemana] = useState<number[]>([]);
  const [intervaloDias, setIntervaloDias] = useState<number>(1);
  const [horariosList, setHorariosList] = useState<string[]>(['09:00']);
  const [dataFimRecorrencia, setDataFimRecorrencia] = useState('');

  const { toast } = useToast();

  const carregarAgendamentos = async () => {
    setLoading(true);
    try {
      const response = await api.get('/agendamentos', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          status: statusFilter || undefined
        }
      });
      setMensagens(response.data.data.agendamentos || []);
      setTotal(response.data.data.total || 0);
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error.response?.data?.message || 'Erro ao carregar agendamentos',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const carregarContatos = async (reset = false) => {
    if (loadingContatos || (reset === false && !hasMoreContatos)) return;
    
    setLoadingContatos(true);
    try {
      const pageToLoad = reset ? 1 : contatosPage;
      const response = await api.get('/contatos', { 
        params: { 
          pageSize: 50, 
          page: pageToLoad,
          search: contatosSearch 
        } 
      });
      
      const contatosData = response.data.contatos || response.data.data || response.data || [];
      const hasMore = contatosData.length === 50;
      
      const options: SelectOption[] = contatosData.map((c: any) => ({
        value: c.id,
        label: `${c.nome || 'Sem nome'} (${c.telefone})`,
        ...c
      }));
      
      if (reset) {
        setContatosOptions(options);
        setContatosPage(2);
      } else {
        setContatosOptions(prev => {
          const existingIds = new Set(prev.map(o => o.value));
          const newOptions = options.filter(o => !existingIds.has(o.value));
          return [...prev, ...newOptions];
        });
        setContatosPage(prev => prev + 1);
      }
      
      setHasMoreContatos(hasMore);
    } catch (error: any) {
      console.error('Erro ao carregar contatos', error);
      toast({
        title: 'Erro',
        description: error.response?.data?.message || 'Erro ao carregar contatos',
        variant: 'destructive'
      });
    } finally {
      setLoadingContatos(false);
    }
  };

  const carregarInstancias = async () => {
    try {
      const response = await api.get('/instancias');
      setInstancias(response.data.instancias || response.data || []);
    } catch (error) {
      console.error('Erro ao carregar instâncias', error);
    }
  };

  const carregarMensagensRapidas = async () => {
    setLoadingQuickMsgs(true);
    try {
      const response = await api.get('/mensagens-rapidas', {
        params: { search: quickMsgsFilter, pageSize: 100 }
      });
      setQuickMessages(response.data.data || response.data.mensagens || []);
    } catch (error) {
      console.error('Erro ao carregar mensagens rápidas', error);
    } finally {
      setLoadingQuickMsgs(false);
    }
  };

  const carregarBlocosDisponiveis = async () => {
    try {
      const response = await api.get('/mensagens-rapidas/categorias');
      setBlocosDisponiveis(Array.isArray(response.data) ? response.data : response.data.categorias || response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar blocos', error);
    }
  };

  const selecionarMensagemRapida = (msg: any) => {
    setConteudo(prev => {
      const separador = prev.trim() ? '\n---\n' : '';
      return prev + separador + (msg.mensagem || '');
    });
    setShowQuickPanel(false);
  };

  useEffect(() => {
    if (showQuickPanel) {
      const timer = setTimeout(() => carregarMensagensRapidas(), 300);
      return () => clearTimeout(timer);
    }
  }, [quickMsgsFilter, showQuickPanel]);

  useEffect(() => {
    carregarAgendamentos();
  }, [page, rowsPerPage, statusFilter]);

  useEffect(() => {
    if (showModal) {
      carregarContatos(true);
      carregarInstancias();
      carregarMensagensRapidas();
      carregarBlocosDisponiveis();
    } else {
      // Limpar estados do form ao fechar
      setContatosOptions([]);
      setSelectedContacts([]);
      setConteudo('');
      setDataAgendamento('');
      setInstanciaSelecionada('AUTO');
      setContatosSearch('');
      setContatosPage(1);
      setHasMoreContatos(true);
      setModoAgendamento('personalizada');
      setBlocoAgendamentoId('');
      setIntervaloBloco(30);
      setShowQuickPanel(false);
      setIsRecorrente(false);
      setDiasSemana([]);
      setHorariosList(['09:00']);
      setDataFimRecorrencia('');
      setListaTelefones('');
      setArquivoLista(null);
      setIntervaloLista(30);
      if (listaFileInputRef.current) listaFileInputRef.current.value = '';
    }
  }, [showModal]);

  // Recarregar contatos quando a busca mudar
  useEffect(() => {
    if (showModal) {
      carregarContatos(true);
    }
  }, [contatosSearch]);

  // Socket listener para atualização de status em tempo real
  useEffect(() => {
    const handleAgendamentoStatus = (data: { id: string; status: string }) => {
      setMensagens(prev =>
        prev.map(m => (m.id === data.id ? { ...m, status: data.status as MensagemAgendada['status'] } : m))
      );
    };

    const setup = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        const socket = await socketService.connect(token);
        socketService.on('agendamento_status_atualizado', handleAgendamentoStatus);
      }
    };
    setup();

    return () => {
      socketService.off('agendamento_status_atualizado', handleAgendamentoStatus);
    };
  }, []);

  const handleSubmit = async () => {
    // Valida a data ANTES de converter: com o campo vazio, toISOString() lança
    if (!dataAgendamento) {
      toast({ title: 'Aviso', description: 'Selecione a data e hora do agendamento', variant: 'destructive' });
      return;
    }
    const dataLocal = new Date(dataAgendamento + ':00');
    if (isNaN(dataLocal.getTime())) {
      toast({ title: 'Aviso', description: 'Data de agendamento inválida', variant: 'destructive' });
      return;
    }
    const dataUtc = dataLocal.toISOString();

    // Validar recorrência se ativada
    if (isRecorrente) {
      if (tipoRecorrencia === 'diasSemana' && diasSemana.length === 0) {
        toast({ title: 'Aviso', description: 'Selecione pelo menos um dia da semana para a repetição', variant: 'destructive' });
        return;
      }
      if (tipoRecorrencia === 'intervaloDias' && (!intervaloDias || intervaloDias < 1)) {
        toast({ title: 'Aviso', description: 'Informe um intervalo de dias válido (mínimo 1)', variant: 'destructive' });
        return;
      }
      if (horariosList.length === 0 || horariosList.some(h => !h)) {
        toast({ title: 'Aviso', description: 'Adicione pelo menos um horário para a repetição', variant: 'destructive' });
        return;
      }
      if (modoAgendamento === 'lista') {
        toast({
          title: 'Aviso',
          description: 'Repetição ainda não é suportada no disparo por lista. Agende disparos separados.',
          variant: 'destructive'
        });
        return;
      }
    }

    if (modoAgendamento === 'lista') {
      const analise = analisarListaTelefones(listaTelefones);
      const temArquivo = !!arquivoLista;

      if (!temArquivo && analise.validos === 0) {
        toast({
          title: 'Aviso',
          description: listaTelefones.trim()
            ? 'Nenhum número válido na lista. Use de 8 a 15 dígitos, com DDI (ex: 5511999998888).'
            : 'Cole a lista de telefones ou envie um arquivo .txt/.csv',
          variant: 'destructive'
        });
        return;
      }
      if (!conteudo || !dataAgendamento) {
        toast({ title: 'Aviso', description: 'Preencha a mensagem e a data/hora', variant: 'destructive' });
        return;
      }

      setLoadingForm(true);
      try {
        let instanciaId: string | undefined = undefined;
        let instanciaUazapiId: string | undefined = undefined;
        if (instanciaSelecionada !== 'AUTO') {
          const [tipoApi, id] = instanciaSelecionada.split('|');
          if (tipoApi === 'EVOLUTION') instanciaId = id;
          else if (tipoApi === 'UAZAPI') instanciaUazapiId = id;
        }

        let response;
        if (arquivoLista) {
          const formData = new FormData();
          formData.append('file', arquivoLista);
          formData.append('conteudo', conteudo);
          formData.append('dataAgendamento', dataUtc);
          formData.append('intervalo', String(intervaloLista));
          if (instanciaId) formData.append('instanciaId', instanciaId);
          if (instanciaUazapiId) formData.append('instanciaUazapiId', instanciaUazapiId);

          response = await api.post('/agendamentos/importar-lista', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } else {
          response = await api.post('/agendamentos/importar-lista', {
            texto: listaTelefones,
            conteudo,
            dataAgendamento: dataUtc,
            intervalo: intervaloLista,
            instanciaId,
            instanciaUazapiId
          });
        }

        const dados = response.data?.data || {};
        const partes: string[] = [`${dados.agendamentosCriados || 0} disparos agendados`];
        if (dados.contatosCriados) partes.push(`${dados.contatosCriados} contatos novos`);
        if (dados.contatosExistentes) partes.push(`${dados.contatosExistentes} já cadastrados`);
        if (dados.invalidos?.length) partes.push(`${dados.invalidos.length} ignorados`);

        toast({
          title: 'Lista agendada',
          description: `${partes.join(' · ')}. Envios espaçados em ${dados.intervaloSeg || intervaloLista}s.`
        });

        setShowModal(false);
        carregarAgendamentos();
      } catch (error: any) {
        toast({
          title: 'Erro',
          description: error.response?.data?.message || 'Erro ao agendar a lista',
          variant: 'destructive'
        });
      } finally {
        setLoadingForm(false);
      }
      return;
    }

    if (modoAgendamento === 'bloco') {
      if (!blocoAgendamentoId || !dataAgendamento) {
        toast({
          title: 'Aviso',
          description: 'Selecione um bloco e preencha a data/hora',
          variant: 'destructive'
        });
        return;
      }
      setLoadingForm(true);
      try {
        let instanciaId: string | undefined = undefined;
        let instanciaUazapiId: string | undefined = undefined;
        if (instanciaSelecionada !== 'AUTO') {
          const [tipoApi, id] = instanciaSelecionada.split('|');
          if (tipoApi === 'EVOLUTION') instanciaId = id;
          else if (tipoApi === 'UAZAPI') instanciaUazapiId = id;
        }

        const payload: any = {
          blocoId: blocoAgendamentoId,
          dataAgendamento: dataUtc,
          intervalo: intervaloBloco,
          instanciaId,
          instanciaUazapiId
        };
        if (isRecorrente && horariosList.length > 0) {
          payload.recorrencia = {
            tipo: tipoRecorrencia,
            ...(tipoRecorrencia === 'diasSemana'
              ? { diasSemana }
              : { intervaloDias }
            ),
            horarios: horariosList,
            ...(dataFimRecorrencia && { dataFim: new Date(dataFimRecorrencia + 'T23:59:59').toISOString() })
          };
        }
        const contatoIds = selectedContacts.map(c => c.value);
        if (contatoIds.length > 0) payload.contatos = contatoIds;

        const response = await api.post('/agendamentos/bloco', payload);

        toast({
          title: 'Sucesso',
          description: response.data.message || `${response.data.data?.total || 0} mensagens agendadas em bloco`
        });
        setShowModal(false);
        carregarAgendamentos();
      } catch (error: any) {
        toast({
          title: 'Erro',
          description: error.response?.data?.message || 'Erro ao agendar bloco',
          variant: 'destructive'
        });
      } finally {
        setLoadingForm(false);
      }
      return;
    }

    if (selectedContacts.length === 0 || !conteudo || !dataAgendamento) {
      toast({
        title: 'Aviso',
        description: 'Preencha todos os campos obrigatórios',
        variant: 'destructive'
      });
      return;
    }

    setLoadingForm(true);
    try {
      let instanciaId: string | undefined = undefined;
      let instanciaUazapiId: string | undefined = undefined;
      
      if (instanciaSelecionada !== 'AUTO') {
        const [tipoApi, id] = instanciaSelecionada.split('|');
        if (tipoApi === 'EVOLUTION') {
          instanciaId = id;
        } else if (tipoApi === 'UAZAPI') {
          instanciaUazapiId = id;
        }
      }

      // Enviar um agendamento para cada contato selecionado
      const promises = selectedContacts.map(contact => {
        const payload: any = {
          contatoId: contact.value,
          conteudo,
          dataAgendamento: dataUtc,
          tipo: 'texto',
          instanciaId,
          instanciaUazapiId
        };
        if (isRecorrente && horariosList.length > 0) {
          payload.recorrencia = {
            tipo: tipoRecorrencia,
            ...(tipoRecorrencia === 'diasSemana'
              ? { diasSemana }
              : { intervaloDias }
            ),
            horarios: horariosList,
            ...(dataFimRecorrencia && { dataFim: new Date(dataFimRecorrencia + 'T23:59:59').toISOString() })
          };
        }
        return api.post('/agendamentos', payload);
      });

      await Promise.all(promises);
      
      toast({
        title: 'Sucesso',
        description: selectedContacts.length > 1 
          ? `${selectedContacts.length} mensagens agendadas com sucesso` 
          : 'Mensagem agendada com sucesso'
      });
      
      setShowModal(false);
      setSelectedContacts([]);
      setConteudo('');
      setDataAgendamento('');
      carregarAgendamentos();
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error.response?.data?.message || 'Erro ao agendar mensagem',
        variant: 'destructive'
      });
    } finally {
      setLoadingForm(false);
    }
  };

  const confirmarCancelamento = async () => {
    if (!itemToCancel) return;
    
    setLoading(true);
    try {
      await api.delete(`/agendamentos/${itemToCancel}`);
      toast({
        title: 'Sucesso',
        description: 'Agendamento cancelado'
      });
      carregarAgendamentos();
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error.response?.data?.message || 'Erro ao cancelar agendamento',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
      setShowCancelModal(false);
      setItemToCancel(null);
    }
  };

  const renderStatus = (status: string) => {
    switch (status) {
      case 'PENDENTE':
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200 rounded-full text-xs flex items-center w-max border border-yellow-200 dark:border-yellow-900"><CalendarClock className="w-3 h-3 mr-1"/> Pendente</span>;
      case 'PROCESSANDO':
        return <span className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200 rounded-full text-xs flex items-center w-max border border-blue-200 dark:border-blue-900"><Loader2 className="w-3 h-3 mr-1 animate-spin"/> Enviando</span>;
      case 'ENVIADA':
        return <span className="px-2 py-1 bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200 rounded-full text-xs flex items-center w-max border border-green-200 dark:border-green-900"><Send className="w-3 h-3 mr-1"/> Enviada</span>;
      case 'ERRO':
        return <span className="px-2 py-1 bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200 rounded-full text-xs flex items-center w-max border border-red-200 dark:border-red-900"><AlertCircle className="w-3 h-3 mr-1"/> Erro</span>;
      case 'CANCELADA':
        return <span className="px-2 py-1 bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200 rounded-full text-xs flex items-center w-max border border-gray-200 dark:border-gray-700"><XCircle className="w-3 h-3 mr-1"/> Cancelada</span>;
      default:
        return <span>{status}</span>;
    }
  };

  const columns = [
    {
      id: 'dataAgendamento' as Extract<keyof MensagemAgendada, string>,
      label: 'Data/Hora de Envio',
      format: (_: string, row: MensagemAgendada) => (
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-700 dark:text-gray-300">
            {new Date(row.dataAgendamento).toLocaleString('pt-BR')}
          </span>
          {row.recorrencia && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 rounded-full text-[10px] font-medium border border-purple-200 dark:border-purple-800"
              title={formatRecorrencia(row.recorrencia)}
            >
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Recorrente
            </span>
          )}
        </div>
      )
    },
    {
      id: 'contato' as any,
      label: 'Contato',
      mobileTitle: true,
      format: (_: any, row: MensagemAgendada) => (
        <div className="flex flex-col">
          <p className="font-medium text-gray-900 dark:text-gray-100">{row.contato?.nome || 'Desconhecido'}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{row.contato?.telefone}</p>
        </div>
      )
    },
    {
      id: 'canal' as any,
      label: 'Canal',
      format: (_: any, row: MensagemAgendada) => {
        const nomeInstancia = row.instancia?.nome || row.instanciaUazapi?.nome || 'Automático';
        return <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{nomeInstancia}</span>;
      }
    },
    {
      id: 'conteudo' as Extract<keyof MensagemAgendada, string>,
      label: 'Mensagem',
      format: (value: string, row: MensagemAgendada) => {
        const texto = value || (row.fileName ? `📎 ${row.fileName}` : '');
        return (
          <div className="max-w-xs truncate text-gray-600 dark:text-gray-400" title={texto}>
            {texto}
          </div>
        );
      }
    },
    {
      id: 'status' as Extract<keyof MensagemAgendada, string>,
      label: 'Status',
      format: (value: string) => renderStatus(value)
    },
    {
      id: 'actions' as any,
      label: 'Ações',
      align: 'right' as const,
      format: (_: any, row: MensagemAgendada) => (
        <div className="flex justify-end space-x-2">
          {row.status === 'PENDENTE' && (
            <Button
              size="sm"
              variant="destructive"
              onClick={() => {
                setItemToCancel(row.id);
                setShowCancelModal(true);
              }}
              className="dark:bg-red-600 dark:hover:bg-red-700 transition-colors"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Cancelar
            </Button>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">Disparo Automático</h2>
          <p className="text-sm text-muted-foreground dark:text-gray-400">
            Agende mensagens para serem enviadas automaticamente para múltiplos contatos.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={statusFilter} onValueChange={(val) => setStatusFilter(val === 'ALL' ? '' : val)}>
            <SelectTrigger className="w-[150px] bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 dark:text-white">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
              <SelectItem value="ALL">Todos os Status</SelectItem>
              <SelectItem value="PENDENTE">Pendente</SelectItem>
              <SelectItem value="ENVIADA">Enviada</SelectItem>
              <SelectItem value="ERRO">Erro</SelectItem>
              <SelectItem value="CANCELADA">Cancelada</SelectItem>
            </SelectContent>
          </Select>
          
          <Button onClick={() => setShowModal(true)} className="bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/20">
            <Plus className="mr-2 h-4 w-4" /> Novo Agendamento
          </Button>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden shadow-sm">
        <DynamicTable
          columns={columns}
          data={mensagens}
          loading={loading}
          total={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          onSort={() => {}}
          onSearch={() => {}}
          searchPlaceholder="Pesquisar..."
          showSearch={false}
          mobileCards
        />
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-[600px] bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 shadow-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900 dark:text-white">Agendar Novo Disparo</DialogTitle>
          </DialogHeader>
          <div className="grid gap-5 py-4">
            {/* Mode Tabs */}
            <div className="flex bg-gray-100 dark:bg-slate-600 rounded-lg p-1">
              <button
                type="button"
                onClick={() => setModoAgendamento('personalizada')}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-all ${
                  modoAgendamento === 'personalizada'
                    ? 'bg-white dark:bg-slate-500 text-blue-600 dark:text-blue-300 shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <Zap className="w-4 h-4" />
                Mensagem Personalizada
              </button>
              <button
                type="button"
                onClick={() => setModoAgendamento('bloco')}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-all ${
                  modoAgendamento === 'bloco'
                    ? 'bg-white dark:bg-slate-500 text-blue-600 dark:text-blue-300 shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <Layers className="w-4 h-4" />
                Bloco de Mensagens
              </button>
              <button
                type="button"
                onClick={() => setModoAgendamento('lista')}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-all ${
                  modoAgendamento === 'lista'
                    ? 'bg-white dark:bg-slate-500 text-blue-600 dark:text-blue-300 shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <ListPlus className="w-4 h-4" />
                Importar Lista
              </button>
            </div>

            {/* Modo: Importar Lista de telefones */}
            {modoAgendamento === 'lista' && (
              <div className="grid gap-2">
                <Label htmlFor="listaTelefones" className="text-sm font-semibold text-gray-700 dark:text-white">
                  Lista de Contatos *
                </Label>
                <Textarea
                  id="listaTelefones"
                  rows={8}
                  value={listaTelefones}
                  onChange={(e) => setListaTelefones(e.target.value)}
                  disabled={!!arquivoLista}
                  placeholder={'5511999998888\n5511988887777\n551133334444'}
                  className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 dark:text-white focus:ring-blue-500 font-mono text-sm disabled:opacity-50"
                />

                <input
                  ref={listaFileInputRef}
                  type="file"
                  accept=".txt,.csv"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    setArquivoLista(file);
                    if (file) setListaTelefones('');
                  }}
                />

                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => listaFileInputRef.current?.click()}
                    className="dark:text-white dark:bg-slate-700 dark:hover:bg-slate-600"
                  >
                    <Upload className="mr-2 h-3.5 w-3.5" />
                    Enviar arquivo .txt ou .csv
                  </Button>
                  {arquivoLista && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setArquivoLista(null);
                        if (listaFileInputRef.current) listaFileInputRef.current.value = '';
                      }}
                      className="text-gray-500 dark:text-gray-300"
                    >
                      <XCircle className="mr-2 h-3.5 w-3.5" />
                      {arquivoLista.name}
                    </Button>
                  )}
                </div>

                {arquivoLista ? (
                  <p className="text-[11px] text-gray-500 dark:text-gray-400">
                    Usando o arquivo <span className="font-medium">{arquivoLista.name}</span>. O campo acima fica
                    desativado — clique no nome do arquivo para removê-lo e colar a lista à mão.
                  </p>
                ) : (
                  <>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                      Um número por linha, só com dígitos, com DDI (ex: 5511999998888). Contatos que ainda não
                      existem serão criados automaticamente.
                    </p>
                    {(() => {
                      const analise = analisarListaTelefones(listaTelefones);
                      if (analise.total === 0) return null;
                      return (
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px]">
                          <span className="text-gray-500 dark:text-gray-400">{analise.total} linha(s)</span>
                          <span className="text-green-600 dark:text-green-400">{analise.validos} válido(s)</span>
                          {analise.duplicados > 0 && (
                            <span className="text-amber-600 dark:text-amber-400">{analise.duplicados} duplicado(s)</span>
                          )}
                          {analise.invalidos > 0 && (
                            <span className="text-red-600 dark:text-red-400">{analise.invalidos} inválido(s)</span>
                          )}
                        </div>
                      );
                    })()}
                  </>
                )}
              </div>
            )}

            {modoAgendamento !== 'lista' && (
            <div className="grid gap-2">
              <Label htmlFor="contato" className="text-sm font-semibold text-gray-700 dark:text-white">
                Contatos Destinatários *
              </Label>
              <SelectWithSearch
                options={contatosOptions}
                value={selectedContacts}
                onChange={setSelectedContacts}
                onSearchChange={setContatosSearch}
                onLoadMore={() => carregarContatos(false)}
                hasMore={hasMoreContatos}
                loading={loadingContatos}
                placeholder="Pesquisar e selecionar contatos..."
                emptyMessage={loadingContatos ? "Carregando contatos..." : "Nenhum contato encontrado"}
                className="w-full"
              />
              <p className="text-[11px] text-gray-500 dark:text-gray-400">Você pode selecionar múltiplos contatos para este disparo.</p>
            </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="instancia" className="text-sm font-semibold text-gray-700 dark:text-white">
                  Canal de Disparo
                </Label>
                <Select value={instanciaSelecionada} onValueChange={setInstanciaSelecionada}>
                  <SelectTrigger className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 dark:text-white">
                    <SelectValue placeholder="Selecione o canal" />
                  </SelectTrigger>
                  <SelectContent className="dark:bg-slate-700 dark:border-slate-600">
                    <SelectItem value="AUTO">Automático (Aleatório)</SelectItem>
                    {instancias.map(i => (
                      <SelectItem key={i.id} value={`${i.tipoApi || 'UAZAPI'}|${i.id}`}>
                        {i.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="data" className="text-sm font-semibold text-gray-700 dark:text-white">
                  Data e Hora de Envio *
                </Label>
                <Input
                  id="data"
                  type="datetime-local"
                  value={dataAgendamento}
                  onChange={(e) => setDataAgendamento(e.target.value)}
                  className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 dark:text-white focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Recorrência (não se aplica ao disparo por lista) */}
            {modoAgendamento !== 'lista' && (
            <div className="border border-gray-200 dark:border-slate-600 rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <Label className="text-sm font-semibold text-gray-700 dark:text-white cursor-pointer">
                    Repetir este agendamento
                  </Label>
                </div>
                <Switch
                  checked={isRecorrente}
                  onCheckedChange={setIsRecorrente}
                  className="data-[state=checked]:bg-purple-600"
                />
              </div>

              {isRecorrente && (
                <div className="space-y-4 transition-all duration-200">
                  {/* Tipo de Recorrência */}
                  <div className="grid gap-2">
                    <Label className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                      Tipo de Recorrência
                    </Label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setTipoRecorrencia('diasSemana')}
                        className={`flex-1 h-10 text-sm font-medium rounded-lg transition-all border flex items-center justify-center gap-2 ${
                          tipoRecorrencia === 'diasSemana'
                            ? 'bg-purple-600 text-white border-purple-600 shadow-sm shadow-purple-300/30 dark:shadow-purple-900/40'
                            : 'bg-white dark:bg-slate-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-slate-500 hover:border-purple-300 dark:hover:border-purple-500 hover:text-purple-600 dark:hover:text-purple-300'
                        }`}
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Dias da Semana
                      </button>
                      <button
                        type="button"
                        onClick={() => setTipoRecorrencia('intervaloDias')}
                        className={`flex-1 h-10 text-sm font-medium rounded-lg transition-all border flex items-center justify-center gap-2 ${
                          tipoRecorrencia === 'intervaloDias'
                            ? 'bg-purple-600 text-white border-purple-600 shadow-sm shadow-purple-300/30 dark:shadow-purple-900/40'
                            : 'bg-white dark:bg-slate-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-slate-500 hover:border-purple-300 dark:hover:border-purple-500 hover:text-purple-600 dark:hover:text-purple-300'
                        }`}
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        A Cada X Dias
                      </button>
                    </div>
                  </div>

                  {/* Modo: Dias da Semana */}
                  {tipoRecorrencia === 'diasSemana' && (
                    <div className="grid gap-2">
                      <Label className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                        Dias da Semana
                      </Label>
                      <div className="flex gap-1.5">
                        {DIAS_SEMANA_LABELS.map((label, idx) => {
                          const ativo = diasSemana.includes(idx);
                          return (
                            <button
                              key={idx}
                              type="button"
                              onClick={() =>
                                setDiasSemana(prev =>
                                  ativo ? prev.filter(d => d !== idx) : [...prev, idx].sort()
                                )
                              }
                              className={`flex-1 h-10 text-sm font-medium rounded-lg transition-all border ${
                                ativo
                                  ? 'bg-purple-600 text-white border-purple-600 shadow-sm shadow-purple-300/30 dark:shadow-purple-900/40'
                                  : 'bg-white dark:bg-slate-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-slate-500 hover:border-purple-300 dark:hover:border-purple-500 hover:text-purple-600 dark:hover:text-purple-300'
                              }`}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                      {diasSemana.length === 0 && (
                        <p className="text-[11px] text-amber-600 dark:text-amber-400">
                          Selecione pelo menos um dia da semana
                        </p>
                      )}
                    </div>
                  )}

                  {/* Modo: Intervalo de Dias */}
                  {tipoRecorrencia === 'intervaloDias' && (
                    <div className="grid gap-2">
                      <Label className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                        Repetir a cada
                      </Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min={1}
                          max={365}
                          value={intervaloDias}
                          onChange={(e) => setIntervaloDias(Math.max(1, parseInt(e.target.value) || 1))}
                          className="w-20 bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 dark:text-white focus:ring-purple-500 text-center font-semibold"
                        />
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {intervaloDias === 1 ? 'dia' : 'dias'}
                        </span>
                      </div>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400">
                        A mensagem será reenviada automaticamente a cada {intervaloDias} {intervaloDias === 1 ? 'dia' : 'dias'}.
                      </p>
                    </div>
                  )}

                  {/* Horários */}
                  <div className="grid gap-2">
                    <Label className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                      Horários de Disparo
                    </Label>
                    <div className="space-y-2">
                      {horariosList.map((horario, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <Input
                            type="time"
                            value={horario}
                            onChange={(e) => {
                              const novos = [...horariosList];
                              novos[idx] = e.target.value;
                              setHorariosList(novos);
                            }}
                            className="w-32 bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 dark:text-white focus:ring-purple-500"
                          />
                          {horariosList.length > 1 && (
                            <button
                              type="button"
                              onClick={() => setHorariosList(prev => prev.filter((_, i) => i !== idx))}
                              className="p-1.5 text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors rounded hover:bg-red-50 dark:hover:bg-red-900/20"
                              title="Remover horário"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                          {idx === horariosList.length - 1 && (
                            <button
                              type="button"
                              onClick={() => setHorariosList(prev => [...prev, '09:00'])}
                              className="p-1.5 text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 transition-colors rounded hover:bg-purple-50 dark:hover:bg-purple-900/20"
                              title="Adicionar horário"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                              </svg>
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Data Final (opcional) */}
                  <div className="grid gap-2">
                    <Label className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                      Data Final <span className="text-gray-400 dark:text-gray-500 font-normal normal-case">(opcional)</span>
                    </Label>
                    <Input
                      type="date"
                      value={dataFimRecorrencia}
                      onChange={(e) => setDataFimRecorrencia(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full sm:w-48 bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 dark:text-white focus:ring-purple-500"
                    />
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                      Deixe em branco para repetir indefinidamente.
                    </p>
                  </div>
                </div>
              )}
            </div>
            )}

            {/* Mensagem: personalize o conteúdo nos modos "personalizada" e "lista" */}
            {(modoAgendamento === 'personalizada' || modoAgendamento === 'lista') && (
              <div className="grid gap-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="conteudo" className="text-sm font-semibold text-gray-700 dark:text-white">
                    Mensagem *
                  </Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowQuickPanel(!showQuickPanel)}
                    className="text-xs dark:bg-slate-600 dark:text-white dark:border-slate-500"
                  >
                    <Zap className="w-3 h-3 mr-1" />
                    Mensagens Rápidas
                  </Button>
                </div>
                <Textarea
                  id="conteudo"
                  placeholder="Digite a mensagem a ser enviada..."
                  value={conteudo}
                  onChange={(e) => setConteudo(e.target.value)}
                  rows={5}
                  className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 dark:text-white resize-none focus:ring-blue-500"
                />
                <p className="text-[11px] text-gray-500 dark:text-gray-400">Dica: Use quebras de linha para tornar a mensagem mais legível.</p>

                {/* Quick Message Panel */}
                {showQuickPanel && (
                  <div className="border border-gray-200 dark:border-slate-500 rounded-lg overflow-hidden">
                    <div className="p-2 bg-gray-50 dark:bg-slate-600 border-b border-gray-200 dark:border-slate-500">
                      <Input
                        placeholder="Filtrar mensagens rápidas..."
                        value={quickMsgsFilter}
                        onChange={(e) => setQuickMsgsFilter(e.target.value)}
                        className="h-8 text-sm bg-white dark:bg-slate-700 dark:text-white"
                      />
                    </div>
                    <div className="max-h-40 overflow-y-auto divide-y divide-gray-100 dark:divide-slate-600">
                      {loadingQuickMsgs ? (
                        <div className="flex items-center justify-center p-4 text-sm text-gray-500 dark:text-gray-400">
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Carregando...
                        </div>
                      ) : quickMessages.length === 0 ? (
                        <div className="p-4 text-sm text-gray-500 dark:text-gray-400 text-center">
                          Nenhuma mensagem rápida encontrada
                        </div>
                      ) : (
                        quickMessages
                          .filter((m: any) =>
                            !quickMsgsFilter ||
                              m.chave?.toLowerCase().includes(quickMsgsFilter.toLowerCase()) ||
                            m.mensagem?.toLowerCase().includes(quickMsgsFilter.toLowerCase())
                          )
                          .map((msg: any) => (
                            <button
                              key={msg.id}
                              type="button"
                              onClick={() => selecionarMensagemRapida(msg)}
                              className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors"
                            >
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {msg.chave || 'Sem título'}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                {msg.mensagem}
                              </p>
                            </button>
                          ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Modo: Bloco de Mensagens */}
            {modoAgendamento === 'bloco' && (
              <>
                <div className="grid gap-2">
                  <Label htmlFor="blocoAgendamento" className="text-sm font-semibold text-gray-700 dark:text-white">
                    Bloco de Mensagens *
                  </Label>
                  <Select value={blocoAgendamentoId} onValueChange={setBlocoAgendamentoId}>
                    <SelectTrigger className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 dark:text-white">
                      <SelectValue placeholder="Selecione o bloco" />
                    </SelectTrigger>
                    <SelectContent className="dark:bg-slate-700 dark:border-slate-600">
                      {blocosDisponiveis.map((cat: any) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.nome} {cat._count?.blocos ? `(${cat._count.blocos})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400">
                    Todas as mensagens deste bloco serão agendadas em sequência.
                  </p>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="intervalo" className="text-sm font-semibold text-gray-700 dark:text-white">
                    Intervalo entre mensagens (segundos)
                  </Label>
                  <Input
                    id="intervalo"
                    type="number"
                    min={10}
                    max={300}
                    value={intervaloBloco}
                    onChange={(e) => setIntervaloBloco(Number(e.target.value))}
                    className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 dark:text-white focus:ring-blue-500"
                  />
                  <p className="text-[11px] text-gray-500 dark:text-gray-400">
                    Recomendado: 20-40s para evitar bloqueio do WhatsApp. Mínimo 10s.
                  </p>
                </div>
              </>
            )}

            {/* Modo: Importar Lista - ritmo de envio */}
            {modoAgendamento === 'lista' && (
              <div className="grid gap-2">
                <Label htmlFor="intervaloLista" className="text-sm font-semibold text-gray-700 dark:text-white">
                  Intervalo entre envios (segundos)
                </Label>
                <Input
                  id="intervaloLista"
                  type="number"
                  min={5}
                  max={600}
                  value={intervaloLista}
                  onChange={(e) => setIntervaloLista(Number(e.target.value))}
                  className="bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 dark:text-white focus:ring-blue-500"
                />
                <p className="text-[11px] text-gray-500 dark:text-gray-400">
                  Cada contato recebe a mensagem {intervaloLista || 0}s após o anterior. Recomendado: 30-60s para
                  listas grandes. O sistema despacha no máximo 60 envios por minuto.
                </p>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowModal(false)} className="dark:bg-slate-600 dark:text-white dark:border-slate-500">
              Cancelar
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={loadingForm}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6"
            >
              {loadingForm ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <CalendarClock className="mr-2 h-4 w-4" />
                  {modoAgendamento === 'bloco'
                    ? 'Agendar Bloco'
                    : modoAgendamento === 'lista'
                      ? 'Agendar Lista'
                      : 'Agendar Disparo'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
        <DialogContent className="dark:bg-slate-700 border-gray-200 dark:border-slate-600 shadow-xl">
          <DialogHeader>
            <DialogTitle className="text-gray-900 dark:text-white flex items-center gap-2">
              <AlertCircle className="text-red-500" size={20} />
              Confirmar Cancelamento
            </DialogTitle>
          </DialogHeader>
          <p className="py-4 text-gray-600 dark:text-gray-400">
            Tem certeza que deseja cancelar este agendamento? Ele não será mais enviado para o destinatário.
          </p>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowCancelModal(false)} className="dark:bg-slate-800 dark:text-white dark:border-slate-700">
              Voltar
            </Button>
            <Button variant="destructive" onClick={confirmarCancelamento} disabled={loading} className="bg-red-600 hover:bg-red-700">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sim, Cancelar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};